import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getOfficerPluralLabel } from '@/utils/auth';
import { DynamicFormRenderer } from '../DynamicFormRenderer';
import type {
  FormFieldData,
  FormSectionData,
} from '@/services/api-types';
import type { FieldDraft } from '../builder/schema-draft';
import type { UseSchemaDraftResult } from '../builder/useSchemaDraft';
import {
  draftToEditorSchema,
  isEditorField,
  isEditorSection,
} from './draft-to-editor-schema';
import { WysiwygTopToolbar } from './WysiwygTopToolbar';
import { WysiwygFieldShell } from './WysiwygFieldShell';
import { WysiwygSectionShell } from './WysiwygSectionShell';
import { WysiwygFieldInspector } from './WysiwygFieldInspector';
import { WysiwygSectionInspector } from './WysiwygSectionInspector';

export interface WysiwygSchemaEditorProps {
  draft: UseSchemaDraftResult;
  onRequestSearchableToggle?: (field: FieldDraft) => void;
  /** Disable interactions during save. */
  disabled?: boolean;
}

/**
 * The WYSIWYG schema editor — replaces the form-based SchemaBuilder.
 *
 * Layout: top toolbar (form name / attachTo / show-all + add-section) + a
 * canvas that renders the schema via DynamicFormRenderer. The canvas itself
 * is a pristine preview — fields and sections look exactly the same as the
 * read-only form an officer sees. Selection draws a colored outline around the
 * picked element via `outline` (no layout shift) and slides in a side-panel
 * inspector with the full edit surface.
 *
 * Selection model: at most one selected field OR one selected section. Click
 * a field anywhere → it's selected. Click a section's title bar → section
 * is selected. Click empty canvas (between sections) → deselect.
 *
 * Desktop-only: under ~900px the canvas hides and a banner directs the user
 * to the Advanced JSON tab.
 */
export const WysiwygSchemaEditor = memo(function WysiwygSchemaEditor({
  draft,
  onRequestSearchableToggle,
  disabled = false,
}: WysiwygSchemaEditorProps) {
  const officersLabel = useMemo(() => getOfficerPluralLabel().toLowerCase(), []);
  const [forceShowAll, setForceShowAll] = useState(false);
  const [selectedFieldUiId, setSelectedFieldUiId] = useState<string | null>(null);
  const [selectedSectionUiId, setSelectedSectionUiId] = useState<string | null>(null);
  // Set when the user clicks Add section so the next render can scroll the new
  // section into view. Cleared after the scroll fires.
  const [pendingScrollSectionUiId, setPendingScrollSectionUiId] = useState<string | null>(null);
  // Live "try" values — let the user test the form with sample input. Resets
  // on schema swap so we don't carry stale values across forms.
  const [tryValues, setTryValues] = useState<Record<string, unknown>>({});
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Reset transient state when the schema's identity changes — guards against
  // selection bleed when the user opens a different form.
  useEffect(() => {
    setTryValues({});
    setSelectedFieldUiId(null);
    setSelectedSectionUiId(null);
  }, [draft.draft.id]);

  // Resolved selected field + its section + position.
  const fieldSelection = useMemo(() => {
    if (!selectedFieldUiId) return null;
    for (const s of draft.draft.sections) {
      const f = s.fields.find((ff) => ff.uiId === selectedFieldUiId);
      if (f) return { section: s, field: f, fieldIndex: s.fields.indexOf(f) };
    }
    return null;
  }, [selectedFieldUiId, draft.draft.sections]);

  const sectionSelection = useMemo(() => {
    if (!selectedSectionUiId) return null;
    const s = draft.draft.sections.find((sd) => sd.uiId === selectedSectionUiId);
    if (!s) return null;
    return { section: s, sectionIndex: draft.draft.sections.indexOf(s) };
  }, [selectedSectionUiId, draft.draft.sections]);

  // earlierFields = every field in earlier sections + earlier fields in same
  // section. Used by the inspector for showIf scope and formula identifiers.
  const earlierFields = useMemo(() => {
    if (!fieldSelection) return [];
    const out: FieldDraft[] = [];
    for (const s of draft.draft.sections) {
      if (s.uiId === fieldSelection.section.uiId) {
        for (const f of s.fields) {
          if (f.uiId === fieldSelection.field.uiId) break;
          out.push(f);
        }
        break;
      }
      for (const f of s.fields) out.push(f);
    }
    return out;
  }, [fieldSelection, draft.draft.sections]);

  // Wraps draft.addSection so the new section also (a) becomes the selected
  // section — opens the inspector with its title input ready to edit — and
  // (b) gets scrolled into view, since on a long form the new section lands at
  // the bottom and would otherwise be off-screen.
  const handleAddSection = useCallback(() => {
    const uiId = draft.addSection();
    setSelectedFieldUiId(null);
    setSelectedSectionUiId(uiId);
    setPendingScrollSectionUiId(uiId);
  }, [draft]);

  // After Add section: scroll the newly-appended shell into view. Runs after
  // the section's DOM node is mounted (useEffect fires post-commit).
  useEffect(() => {
    if (!pendingScrollSectionUiId) return;
    const el = document.querySelector(
      `.wysiwyg-section-shell[data-ui-id="${pendingScrollSectionUiId}"]`,
    );
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setPendingScrollSectionUiId(null);
  }, [pendingScrollSectionUiId, draft.draft.sections.length]);

  const handleSelectField = useCallback((uiId: string | null) => {
    setSelectedFieldUiId(uiId);
    setSelectedSectionUiId(null);
  }, []);

  const handleSelectSection = useCallback((uiId: string | null) => {
    setSelectedSectionUiId(uiId);
    setSelectedFieldUiId(null);
  }, []);

  const handleCloseInspector = useCallback(() => {
    setSelectedFieldUiId(null);
    setSelectedSectionUiId(null);
  }, []);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    // Only deselect on truly empty-canvas clicks. Clicks inside a field or
    // section shell are owned by that shell — if we unconditionally cleared
    // selection here, the bubble from a fresh selection would clear it.
    const target = e.target as HTMLElement;
    if (target.closest('.wysiwyg-field-shell')) return;
    if (target.closest('.wysiwyg-section-shell')) return;
    setSelectedFieldUiId(null);
    setSelectedSectionUiId(null);
  }, []);

  // Build the editor-ready schema once per draft change.
  const editorSchema = useMemo(() => draftToEditorSchema(draft.draft), [draft.draft]);

  const renderFieldWrapper = useCallback(
    (args: { field: FormFieldData; defaultNode: React.ReactNode }) => {
      const f = args.field;
      if (!isEditorField(f)) return args.defaultNode;
      const section = draft.draft.sections.find((s) => s.uiId === f.__sectionUiId);
      if (!section) return args.defaultNode;
      const fieldDraft = section.fields.find((ff) => ff.uiId === f.__uiId);
      if (!fieldDraft) return args.defaultNode;

      return (
        <WysiwygFieldShell
          key={f.__uiId}
          field={fieldDraft}
          selected={selectedFieldUiId === f.__uiId}
          defaultNode={args.defaultNode}
          onSelect={handleSelectField}
        />
      );
    },
    [draft.draft.sections, selectedFieldUiId, handleSelectField],
  );

  const renderSectionWrapper = useCallback(
    (args: { section: FormSectionData; defaultNode: React.ReactNode }) => {
      const s = args.section;
      if (!isEditorSection(s)) return args.defaultNode;
      const sectionDraft = draft.draft.sections.find((sd) => sd.uiId === s.__uiId);
      if (!sectionDraft) return args.defaultNode;

      return (
        <WysiwygSectionShell
          key={s.__uiId}
          section={sectionDraft}
          selected={selectedSectionUiId === s.__uiId}
          defaultNode={args.defaultNode}
          onSelect={handleSelectSection}
        />
      );
    },
    [draft.draft.sections, selectedSectionUiId, handleSelectSection],
  );

  const hasSelection = fieldSelection != null || sectionSelection != null;

  // Empty canvas
  if (draft.draft.sections.length === 0) {
    return (
      <div className="wysiwyg-editor">
        <WysiwygTopToolbar
          draft={draft}
          forceShowAll={forceShowAll}
          onForceShowAllChange={setForceShowAll}
          disabled={disabled}
          onAddSection={handleAddSection}
        />
        <div className="wysiwyg-canvas wysiwyg-canvas--empty">
          <div className="wysiwyg-empty-state">
            <p>Start by adding a section. Each section is a card on the form your {officersLabel} will see, like "Property Details" or "Emergency Contact".</p>
            <button
              type="button"
              className="kstc-btn kstc-btn--primary"
              onClick={handleAddSection}
              disabled={disabled}
            >
              + Add section
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile fallback
  if (isNarrow) {
    return (
      <div className="wysiwyg-editor">
        <WysiwygTopToolbar
          draft={draft}
          forceShowAll={forceShowAll}
          onForceShowAllChange={setForceShowAll}
          disabled={disabled}
          onAddSection={handleAddSection}
        />
        <div className="wysiwyg-mobile-banner">
          <strong>Form editing works best on a larger screen.</strong>
          <p>
            Open this page on a desktop or tablet to design forms. Your {officersLabel} can still fill them out on a phone.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        'wysiwyg-editor' + (hasSelection ? ' wysiwyg-editor--inspector-open' : '')
      }
    >
      <WysiwygTopToolbar
        draft={draft}
        forceShowAll={forceShowAll}
        onForceShowAllChange={setForceShowAll}
        disabled={disabled}
        onAddSection={handleAddSection}
      />
      <div className="wysiwyg-stage">
        <div className="wysiwyg-canvas" onMouseDown={handleCanvasMouseDown}>
          <DynamicFormRenderer
            schemaVersion={editorSchema}
            values={tryValues}
            onChange={setTryValues}
            forceShowAll={forceShowAll}
            renderFieldWrapper={renderFieldWrapper}
            renderSectionWrapper={renderSectionWrapper}
          />
        </div>
        {fieldSelection && (
          <WysiwygFieldInspector
            field={fieldSelection.field}
            sectionUiId={fieldSelection.section.uiId}
            fieldIndex={fieldSelection.fieldIndex}
            totalFieldsInSection={fieldSelection.section.fields.length}
            earlierFields={earlierFields}
            draft={draft}
            onClose={handleCloseInspector}
            onRequestSearchableToggle={onRequestSearchableToggle}
          />
        )}
        {sectionSelection && (
          <WysiwygSectionInspector
            section={sectionSelection.section}
            sectionIndex={sectionSelection.sectionIndex}
            totalSections={draft.draft.sections.length}
            draft={draft}
            onClose={handleCloseInspector}
          />
        )}
      </div>
    </div>
  );
});
